/**
 * One adoption listing, as a moderator needs to see it.
 *
 * The question this page answers is "should this stay up", and answering it
 * needs three things the old page did not show: the photos (a listing is its
 * pictures), the contact block an adopter pays to unlock, and the history of
 * what admins have already done to it.
 *
 * The people on both sides of this record are ordinary customers rather than
 * businesses, so every name here links back to their user record — a listing
 * that looks wrong is usually one of several from the same account.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  CalendarDays,
  Copy,
  Eye,
  EyeOff,
  Heart,
  History,
  ImageOff,
  Images,
  MapPin,
  Phone,
  Trash2,
  User,
  Users,
  Loader2,
  RotateCcw,
  Wallet,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import {
  DetailPanel,
  Field,
  FieldList,
  PanelEmpty,
  PanelSkeleton,
} from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import { notify } from '@/components/ui/toaster';
import { ago, date_time, inr, title_case } from '@/lib/format';
import {
  use_adoption_detail,
  use_moderate_listing,
  use_adoption_refunds,
  use_set_adoption_adopter,
  use_refund_adoption_participant,
  use_refund_all_adoption_participants,
} from '@/features/adoption/hooks';
import {
  ADOPTION_STATUS_LABELS,
  type AdoptionDetail,
  type AdoptionListingStatus,
  type AdoptionModerationAction,
  type AdoptionParticipant,
} from '@/features/adoption/types';
import { cn } from '@/lib/utils';

const STATUS_TONE: Record<
  AdoptionListingStatus,
  'success' | 'warning' | 'neutral' | 'destructive' | 'info'
> = {
  PUBLISHED: 'success',
  DRAFT: 'warning',
  ADOPTED: 'info',
  CLOSED: 'neutral',
  HIDDEN: 'warning',
  REMOVED: 'destructive',
};

const REQUEST_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  CONFIRMED: 'success',
  PENDING_PAYMENT: 'warning',
  CANCELLED: 'neutral',
};

export function AdoptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: listing, isLoading, error } = use_adoption_detail(id);

  const [action, set_action] = useState<AdoptionModerationAction | null>(null);
  const [modal_error, set_modal_error] = useState<string | null>(null);
  const moderate = use_moderate_listing(id ?? '');

  function apply(reason: string) {
    if (!action) return;
    set_modal_error(null);
    moderate.mutate(
      { action, reason },
      {
        onSuccess: (updated) => {
          set_action(null);
          notify.success(
            action === 'hide'
              ? 'Listing hidden'
              : action === 'unhide'
                ? 'Listing restored'
                : 'Listing removed',
            {
              description: `Now ${ADOPTION_STATUS_LABELS[updated.status].toLowerCase()}. The person who posted it has been told why.`,
            },
          );
        },
        onError: (err) => {
          set_modal_error(err instanceof Error ? err.message : 'Could not apply that');
          notify.error(err, 'Could not moderate the listing');
        },
      },
    );
  }

  const metrics: Metric[] = useMemo(
    () => [
      {
        key: 'photos',
        label: 'Photos',
        value: listing?.photo_count ?? 0,
        // A listing with no picture is the commonest thing a moderator acts on,
        // so the tile says so rather than showing a neutral zero.
        tone: listing && listing.photo_count === 0 ? 'warning' : 'default',
        hint: listing && listing.photo_count === 0 ? 'Nothing for an adopter to see' : undefined,
        icon: Images,
        loading: isLoading,
      },
      {
        key: 'interest',
        label: 'Adopters interested',
        value: listing?.request_count ?? 0,
        icon: Users,
        loading: isLoading,
      },
      {
        key: 'paid',
        label: 'Contact fees collected',
        value: listing ? inr(listing.fees_collected_inr) : undefined,
        hint: 'Paid by adopters to unlock the number',
        icon: Wallet,
        loading: isLoading,
      },
      {
        key: 'contact',
        label: 'Contact number',
        value: listing ? (listing.phone_verified ? 'Verified' : 'Unverified') : undefined,
        tone: listing && !listing.phone_verified ? 'warning' : 'success',
        hint: listing?.phone_verified_at ? date_time(listing.phone_verified_at) : 'Never proven',
        icon: Phone,
        loading: isLoading,
      },
      {
        key: 'decisions',
        label: 'Admin decisions',
        value: listing?.moderation_audits.length ?? 0,
        icon: History,
        loading: isLoading,
      },
    ],
    [listing, isLoading],
  );

  const can_hide = listing && listing.status !== 'HIDDEN' && listing.status !== 'REMOVED';
  const can_unhide = listing?.status === 'HIDDEN';
  const can_remove = listing && listing.status !== 'REMOVED';

  return (
    <DashboardLayout
      title={listing ? listing_title(listing) : 'Listing'}
      subtitle={
        listing
          ? [listing.city, listing.state].filter(Boolean).join(', ') || 'No location given'
          : undefined
      }
      breadcrumbs={[{ label: listing_title(listing) }]}
      avatar={
        listing?.primary_photo_thumb_path ? (
          <AuthImage
            path={listing.primary_photo_thumb_path}
            alt=""
            className="h-14 w-14 shrink-0 rounded-panel object-cover"
          />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-panel bg-muted text-muted-foreground">
            <ImageOff aria-hidden="true" className="h-5 w-5" />
          </span>
        )
      }
      title_badges={
        listing ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge
              tone={STATUS_TONE[listing.status]}
              size="sm"
              dot
              data-testid={`listing-status-${listing.status}`}
            >
              {ADOPTION_STATUS_LABELS[listing.status]}
            </Badge>
            {!listing.phone_verified && (
              <Badge tone="warning" size="sm" dot>
                Contact unverified
              </Badge>
            )}
            {listing.photo_count === 0 && (
              <Badge tone="warning" size="sm" dot>
                No photo
              </Badge>
            )}
          </span>
        ) : undefined
      }
      meta={
        listing ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <CalendarDays aria-hidden="true" className="h-3.5 w-3.5" /> Posted{' '}
              {ago(listing.created_at)}
            </span>
            {listing.published_at && <span>Live since {date_time(listing.published_at)}</span>}
            {listing.adopted_at && <span>Adopted {date_time(listing.adopted_at)}</span>}
          </span>
        ) : undefined
      }
      actions={
        listing ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/adoption">Back to listings</Link>
            </Button>
            {can_unhide && (
              <Button size="sm" onClick={() => set_action('unhide')} data-testid="action-unhide">
                <Eye className="h-4 w-4" /> Restore
              </Button>
            )}
            {can_hide && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => set_action('hide')}
                data-testid="action-hide"
              >
                <EyeOff className="h-4 w-4" /> Hide
              </Button>
            )}
            {can_remove && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => set_action('remove')}
                data-testid="action-remove"
              >
                <Trash2 className="h-4 w-4" /> Remove
              </Button>
            )}
          </div>
        ) : undefined
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {error ? (
        <p className="text-data text-destructive">Could not load this listing.</p>
      ) : isLoading || !listing ? (
        <div className="flex flex-col gap-gutter">
          <PanelSkeleton rows={6} />
        </div>
      ) : (
        <div className="flex flex-col gap-gutter" data-testid="adoption-detail">
          {/*
            Removed listings keep their page rather than 404ing — the record of
            what was pulled and why is the point of keeping it.
          */}
          {(listing.status === 'HIDDEN' || listing.status === 'REMOVED') && (
            <div
              className="flex items-start gap-2 rounded-panel bg-warning/10 px-3 py-2.5 text-data text-warning"
              data-testid="moderated-banner"
            >
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">
                  {listing.status === 'HIDDEN'
                    ? 'This listing is hidden from the feed.'
                    : 'This listing was removed.'}
                </strong>{' '}
                {listing.moderation_audits[0]
                  ? `${listing.moderation_audits[0].changed_by_name ?? 'An admin'} ${ago(listing.moderation_audits[0].changed_at)}: “${listing.moderation_audits[0].reason}”`
                  : 'No reason was recorded — this predates the audit trail.'}
              </span>
            </div>
          )}

          <PhotoPanel listing={listing} />

          <div className="grid items-start gap-gutter lg:grid-cols-2">
            <DetailPanel title="The animal" icon={Heart} test_id="animal-panel">
              <FieldList>
                <Field label="Species" value={title_case(listing.species)} />
                <Field label="Breed" value={listing.breed} empty="Not given" />
                <Field label="Age" value={listing.age_label} empty="Not given" />
                <Field
                  label="Sex"
                  value={listing.gender === 'UNKNOWN' ? null : title_case(listing.gender)}
                  empty="Not given"
                />
                {/*
                  Tri-state, deliberately. "Not told us" is not "no", and an
                  adopter deciding whether to take on vet costs is entitled to
                  know which of the two they are looking at.
                */}
                <Field label="Vaccinated" value={<TriState value={listing.vaccinated} />} />
                <Field label="Sterilised" value={<TriState value={listing.sterilized} />} />
                <Field label="Good with" value={listing.good_with} empty="Not stated" />
                <Field label="Health notes" value={listing.health_status} empty="None given" />
                <Field label="Description" value={listing.description} empty="None given" />
              </FieldList>
            </DetailPanel>

            <div className="flex flex-col gap-gutter">
              <DetailPanel
                title="Who to call"
                icon={Phone}
                description="What an adopter gets after paying the fee"
                test_id="contact-panel"
              >
                <FieldList>
                  <Field label="Name" value={listing.contact.contact_name} />
                  <Field
                    label="Phone"
                    mono
                    value={
                      <span className="flex flex-wrap items-center gap-2">
                        <CopyableValue
                          value={listing.contact.contact_phone}
                          label="Contact number"
                        />
                        {listing.phone_verified ? (
                          <Badge tone="success" size="sm" dot>
                            <BadgeCheck className="h-3 w-3" /> Verified
                          </Badge>
                        ) : (
                          <Badge tone="warning" size="sm" dot>
                            Never verified
                          </Badge>
                        )}
                      </span>
                    }
                  />
                  <Field label="Address" value={listing.contact.address_line} empty="Not given" />
                  <Field
                    label="City"
                    value={[listing.city, listing.state].filter(Boolean).join(', ')}
                  />
                  <Field label="Pincode" value={listing.contact.pincode} mono empty="Not given" />
                  <Field
                    label="Map"
                    value={
                      listing.contact.latitude && listing.contact.longitude ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${listing.contact.latitude},${listing.contact.longitude}`}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <MapPin className="h-3.5 w-3.5" /> {listing.contact.latitude},{' '}
                          {listing.contact.longitude}
                        </a>
                      ) : null
                    }
                    empty="No pin dropped"
                  />
                </FieldList>
              </DetailPanel>

              <DetailPanel
                title="Posted by"
                icon={User}
                description="The customer account behind this listing"
                test_id="poster-panel"
              >
                <div className="flex items-center gap-3">
                  <PersonAvatar
                    path={listing.poster.avatar_path ?? null}
                    name={listing.poster.name}
                    seed={listing.poster.id}
                    size="md"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-data font-semibold">
                      {listing.poster.name ?? 'No name on file'}
                    </p>
                    <p className="truncate text-micro text-muted-foreground">
                      {listing.poster.phone ?? 'No phone'}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-auto" asChild>
                    <Link to={`/users/${listing.poster.id}`} data-testid="open-poster">
                      Open their account
                    </Link>
                  </Button>
                </div>
                {/*
                  The most useful next click when a listing looks wrong: one bad
                  post is usually one of several from the same person.
                */}
                <Button variant="outline" size="sm" className="mt-3 w-full" asChild>
                  <Link
                    to={`/adoption?segment=all&q=${encodeURIComponent(listing.poster.phone ?? '')}`}
                    data-testid="poster-other-listings"
                  >
                    Their other listings
                  </Link>
                </Button>
              </DetailPanel>
            </div>
          </div>

          <RequestsPanel listing={listing} />
          <HistoryPanel listing={listing} />
        </div>
      )}

      <ReasonModal
        open={action !== null}
        title={
          action === 'hide'
            ? 'Hide this listing?'
            : action === 'unhide'
              ? 'Restore this listing?'
              : 'Remove this listing?'
        }
        description={
          action === 'hide'
            ? 'It will stop showing in the adoption feed. The reason you give is sent to the person who posted it, and hiding can be undone.'
            : action === 'unhide'
              ? 'It will show in the feed again. The reason you give is sent to the person who posted it.'
              : 'It will be removed for good — removal cannot be undone from here. The reason you give is sent to the person who posted it.'
        }
        confirm_label={action === 'hide' ? 'Hide' : action === 'unhide' ? 'Restore' : 'Remove'}
        variant={action === 'remove' ? 'destructive' : 'default'}
        loading={moderate.isPending}
        error={modal_error}
        on_confirm={apply}
        on_cancel={() => {
          set_action(null);
          set_modal_error(null);
        }}
      />
    </DashboardLayout>
  );
}

/** The listing's photos, at a size a person can actually judge. */
function PhotoPanel({ listing }: { listing: AdoptionDetail }) {
  return (
    <DetailPanel
      title="Photos"
      icon={Images}
      description={
        listing.photos.length === 0
          ? 'None uploaded'
          : `${listing.photos.length} photo${listing.photos.length === 1 ? '' : 's'}, in the order an adopter sees them`
      }
      test_id="photos-panel"
    >
      {listing.photos.length === 0 ? (
        <PanelEmpty icon={ImageOff}>
          No photos on this listing. A live listing with nothing to look at is the commonest reason
          to hide one.
        </PanelEmpty>
      ) : (
        <div className="flex flex-wrap gap-2.5">
          {listing.photos.map((photo) => (
            <a
              key={photo.id}
              href={photo.file_path}
              onClick={(e) => e.preventDefault()}
              className="group relative"
              aria-label="Listing photo"
            >
              <AuthImage
                path={photo.thumb_path ?? photo.file_path}
                alt=""
                className={cn(
                  'h-32 w-32 rounded-panel border object-cover',
                  photo.is_primary ? 'border-primary ring-1 ring-primary/40' : 'border-border',
                )}
              />
              {photo.is_primary && (
                <Badge tone="primary" size="sm" className="absolute bottom-1 left-1">
                  Cover
                </Badge>
              )}
            </a>
          ))}
        </div>
      )}
    </DetailPanel>
  );
}

/**
 * Who wants this animal, and who has paid to be told the number.
 *
 * The fee is the platform's only revenue on adoption, and a CONFIRMED request is
 * a real person who has paid and is now waiting on a stranger to pick up — the
 * most support-sensitive state in this module.
 */
/**
 * Who enquired, who took the pet, and who is owed their fee back.
 *
 * Paying the contact-unlock fee is what reveals the poster's number, so several
 * people paying for one listing is the normal case and only one of them ends up
 * with the pet. Nothing used to return the others' money, and the record of who
 * deserved it lived in a chat log.
 *
 * Marking the adopter and refunding are separate acts on purpose: the adopter is a
 * fact an admin learns from the poster, and the refund is money leaving. Refunding
 * records the debt; an admin moves the money and settles it on the Payments screen,
 * same as every other refund on the platform.
 */
function RequestsPanel({ listing }: { listing: AdoptionDetail }) {
  const refunds_q = use_adoption_refunds(listing.id);
  const set_adopter = use_set_adoption_adopter(listing.id);
  const refund_one = use_refund_adoption_participant(listing.id);
  const refund_all = use_refund_all_adoption_participants(listing.id);
  const view = refunds_q.data;

  const eligible = view?.participants.filter((p) => p.can_refund) ?? [];
  const busy = set_adopter.isPending || refund_one.isPending || refund_all.isPending;
  const by_request = new Map((view?.participants ?? []).map((p) => [p.request_id, p]));

  function mark_adopter(request_id: string, currently: boolean) {
    set_adopter.mutate(currently ? null : request_id, {
      onSuccess: () =>
        notify.success(currently ? 'Adopter cleared' : 'Marked as the adopter', {
          description: currently ? undefined : 'They keep their fee; the others can be refunded.',
        }),
      onError: (err) => notify.error(err, 'Could not set the adopter'),
    });
  }

  function do_refund_one(p: AdoptionParticipant) {
    refund_one.mutate(p.request_id, {
      onSuccess: () =>
        notify.success(`Refund recorded for ${p.adopter_name ?? 'this adopter'}`, {
          description: 'Settle the payment on the Payments screen once the money is sent.',
        }),
      onError: (err) => notify.error(err, 'Could not record the refund'),
    });
  }

  function do_refund_all() {
    refund_all.mutate(undefined, {
      onSuccess: (result) => {
        if (result.failed.length > 0) {
          notify.error(
            new Error(`${result.refunded.length} refunded, ${result.failed.length} failed`),
            'Some refunds could not be recorded',
          );
          return;
        }
        notify.success(
          `${result.refunded.length} refund${result.refunded.length === 1 ? '' : 's'} recorded`,
          { description: 'Settle them on the Payments screen once the money is sent.' },
        );
      },
      onError: (err) => notify.error(err, 'Could not record the refunds'),
    });
  }

  return (
    <DetailPanel
      title="Interested adopters"
      icon={Users}
      description={
        listing.requests.length === 0
          ? 'Nobody has enquired'
          : `${listing.requests.length} enquiry${listing.requests.length === 1 ? '' : 's'} · ${inr(listing.fees_collected_inr)} collected`
      }
      test_id="requests-panel"
    >
      {eligible.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-panel bg-warning/10 px-3 py-2">
          <p className="text-micro text-warning">
            {eligible.length} {eligible.length === 1 ? 'enquirer is' : 'enquirers are'} owed their
            fee back. Recording a refund does not send the money — settle it on the Payments
            screen.
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={do_refund_all}
            data-testid="refund-all"
          >
            {refund_all.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
            Refund all · {inr(view?.outstanding_inr ?? '0')}
          </Button>
        </div>
      )}
      {listing.requests.length === 0 ? (
        <PanelEmpty icon={Users}>No adopter has enquired about this listing yet.</PanelEmpty>
      ) : (
        <ul className="flex flex-col divide-y divide-border/60">
          {listing.requests.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0"
            >
              <PersonAvatar
                path={r.adopter.avatar_path ?? null}
                name={r.adopter.name}
                seed={r.adopter.id}
                size="sm"
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <Link
                  to={`/users/${r.adopter.id}`}
                  className="truncate text-data font-semibold underline-offset-2 hover:text-primary hover:underline"
                >
                  {r.adopter.name ?? 'No name on file'}
                </Link>
                <span className="truncate text-micro text-muted-foreground">
                  {r.adopter.phone ?? 'No phone'} · enquired {ago(r.created_at)}
                </span>
              </span>
              <Badge tone={REQUEST_TONE[r.status] ?? 'neutral'} size="sm" dot>
                {r.status === 'PENDING_PAYMENT' ? 'Fee not paid' : title_case(r.status)}
              </Badge>
              {r.payment && (
                <span className="text-data tabular text-muted-foreground">
                  {inr(r.payment.amount_inr)}
                  {r.payment.captured_at ? '' : ' (uncaptured)'}
                </span>
              )}
              <RefundControls
                participant={by_request.get(r.id)}
                busy={busy}
                on_mark_adopter={mark_adopter}
                on_refund={do_refund_one}
              />
            </li>
          ))}
        </ul>
      )}
    </DetailPanel>
  );
}

/**
 * The refund half of one enquiry row.
 *
 * Split out because the row was already dense, and because `can_refund` /
 * `blocked_reason` come from the server: when a refund is not offered the reason is
 * shown instead, so an admin is never left wondering whether the button is missing
 * or the person is simply not owed anything.
 */
function RefundControls({
  participant,
  busy,
  on_mark_adopter,
  on_refund,
}: {
  participant: AdoptionParticipant | undefined;
  busy: boolean;
  on_mark_adopter: (request_id: string, currently: boolean) => void;
  on_refund: (p: AdoptionParticipant) => void;
}) {
  if (!participant) return null;
  const p = participant;
  const refunded = Number(p.refunded_inr ?? '0') > 0;

  return (
    <span className="flex items-center gap-2">
      {p.is_adopter && (
        <Badge tone="success" size="sm" dot>
          Adopted
        </Badge>
      )}
      {refunded && (
        <Badge tone="neutral" size="sm">
          Refunded {inr(p.refunded_inr ?? '0')}
        </Badge>
      )}
      {/*
        Only offered to someone who actually paid: there is nothing to refund
        otherwise, and nobody can adopt without unlocking the contact first.
      */}
      {p.request_status === 'CONFIRMED' && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => on_mark_adopter(p.request_id, p.is_adopter)}
          data-testid={`mark-adopter-${p.request_id}`}
        >
          {p.is_adopter ? 'Not the adopter' : 'Took the pet'}
        </Button>
      )}
      {p.can_refund ? (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => on_refund(p)}
          data-testid={`refund-${p.request_id}`}
        >
          Refund
        </Button>
      ) : (
        p.blocked_reason &&
        !refunded && (
          <span
            className="text-micro text-muted-foreground"
            data-testid={`refund-blocked-${p.request_id}`}
          >
            {p.blocked_reason}
          </span>
        )
      )}
    </span>
  );
}

/** Every admin decision on this listing, with the reason the poster was given. */
function HistoryPanel({ listing }: { listing: AdoptionDetail }) {
  return (
    <DetailPanel
      title="Moderation history"
      icon={History}
      description="Every admin decision on this listing, and the reason the poster was given"
      test_id="history-panel"
    >
      {listing.moderation_audits.length === 0 ? (
        <PanelEmpty icon={History}>
          No admin has acted on this listing. Decisions made before the audit trail existed are not
          shown here — there was nothing recording them.
        </PanelEmpty>
      ) : (
        <ul className="flex flex-col divide-y divide-border/60">
          {listing.moderation_audits.map((a) => (
            <li key={a.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[a.from_status]} size="sm">
                  {ADOPTION_STATUS_LABELS[a.from_status]}
                </Badge>
                <span className="text-muted-foreground">→</span>
                <Badge tone={STATUS_TONE[a.to_status]} size="sm" dot>
                  {ADOPTION_STATUS_LABELS[a.to_status]}
                </Badge>
                <span className="text-micro text-muted-foreground">
                  {a.changed_by_name ?? 'Unknown admin'} · {date_time(a.changed_at)}
                </span>
              </span>
              <p className="text-data text-muted-foreground">“{a.reason}”</p>
            </li>
          ))}
        </ul>
      )}
    </DetailPanel>
  );
}

/** Yes / No / never told us — three states, not two. */
function TriState({ value }: { value: boolean | null }) {
  if (value === null) return <span className="text-muted-foreground/60">Not stated</span>;
  return (
    <Badge tone={value ? 'success' : 'warning'} size="sm" dot>
      {value ? 'Yes' : 'No'}
    </Badge>
  );
}

function CopyableValue({ value, label }: { value: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular">{value}</span>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          notify.success(`${label} copied`);
        }}
        className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  );
}

function listing_title(listing: AdoptionDetail | undefined): string {
  if (!listing) return 'Listing';
  return [title_case(listing.species), listing.breed].filter(Boolean).join(' · ');
}

export default AdoptionDetailPage;
